import React, { useCallback, useState, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X, Image as ImageIcon, AlertCircle, Check, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { useCategoryImageUpload, UploadedImage } from '@/hooks/useCategoryImageUpload';
import { formatFileSize, isImageFile } from '@/utils/imageUtils';
import { cn } from '@/lib/utils';

interface CategoryImageUploadProps {
  categoryName: string;
  currentImageUrl?: string | null;
  onImageUploaded?: (image: UploadedImage) => void;
  onImageRemoved?: () => void;
  className?: string;
  disabled?: boolean;
  autoUpload?: boolean;
}

export const CategoryImageUpload: React.FC<CategoryImageUploadProps> = ({
  categoryName,
  currentImageUrl,
  onImageUploaded,
  onImageRemoved,
  className,
  disabled = false,
  autoUpload = true
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const {
    isUploading,
    progress,
    uploadedImage,
    error,
    previewUrl,
    isReady,
    hasImage,
    hasPreview,
    progressPercentage,
    uploadImage,
    deleteImage,
    resetUpload,
    handleFileSelect
  } = useCategoryImageUpload({
    maxWidth: 1200,
    maxHeight: 600,
    quality: 0.85
  });

  /**
   * Handle files from dropzone
   */
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (disabled || !categoryName) {
      return;
    }

    const imageFile = acceptedFiles[0];

    if (imageFile && isImageFile(imageFile)) {
      setSelectedFile(imageFile);
      handleFileSelect(imageFile, categoryName, autoUpload);
    }
  }, [disabled, categoryName, handleFileSelect, autoUpload]);

  /**
   * Configure dropzone
   */
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
    fileRejections
  } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
      'image/svg+xml': ['.svg']
    },
    maxSize: 5 * 1024 * 1024, // 5MB
    maxFiles: 1,
    disabled: disabled || !categoryName,
    noClick: hasPreview // Disable click when preview is shown
  });

  /**
   * Handle manual upload
   */
  const handleManualUpload = useCallback(async () => {
    if (selectedFile && categoryName) {
      const result = await uploadImage(selectedFile, categoryName);
      if (result && onImageUploaded) {
        onImageUploaded(result);
      }
    }
  }, [selectedFile, categoryName, uploadImage, onImageUploaded]);

  /**
   * Handle image removal
   */
  const handleRemove = useCallback(async () => {
    if (uploadedImage) {
      const success = await deleteImage(uploadedImage.path);
      if (success && onImageRemoved) {
        onImageRemoved();
      }
    }
    
    resetUpload();
    setSelectedFile(null);
  }, [uploadedImage, deleteImage, onImageRemoved, resetUpload]);

  /**
   * Open file picker manually when preview is shown
   */
  const openFilePicker = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/jpg,image/png,image/webp,image/svg+xml';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file && isImageFile(file) && categoryName) {
        setSelectedFile(file);
        handleFileSelect(file, categoryName, autoUpload);
      }
    };
    input.click();
  }, [categoryName, handleFileSelect, autoUpload]);

  // Determine which image to show
  const displayImageUrl = uploadedImage?.url || currentImageUrl;
  const showPreview = hasPreview || displayImageUrl;

  return (
    <div className={cn('space-y-4', className)}>
      {/* Upload Area */}
      <div
        {...(!showPreview ? getRootProps() : {})}
        className={cn(
          'relative border-2 border-dashed rounded-lg transition-all duration-200',
          'hover:border-primary/50 hover:bg-primary/5',
          isDragActive && 'border-primary bg-primary/10',
          isDragReject && 'border-red-500 bg-red-50',
          disabled && 'opacity-50 cursor-not-allowed',
          !disabled && !showPreview && 'cursor-pointer',
          showPreview ? 'border-border' : 'border-muted-foreground/25'
        )}
      >
        {!showPreview && <input {...getInputProps()} />}
        {showPreview ? (
          // Image Preview
          <div className="relative group">
            <img
              src={previewUrl || displayImageUrl || ''}
              alt={`${categoryName} category image`}
              className="w-full h-48 object-cover rounded-lg"
            />
            
            {/* Overlay with actions */}
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-lg flex items-center justify-center">
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    openFilePicker();
                  }}
                  disabled={disabled || isUploading}
                >
                  <Upload className="h-4 w-4 mr-1" />
                  Replace
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove();
                  }}
                  disabled={disabled || isUploading}
                >
                  <X className="h-4 w-4 mr-1" />
                  Remove
                </Button>
              </div>
            </div>

            {/* Upload status indicator */}
            {isUploading && (
              <div className="absolute top-2 right-2">
                <Badge variant="secondary" className="bg-blue-500 text-white">
                  <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                  Uploading...
                </Badge>
              </div>
            )}

            {uploadedImage && (
              <div className="absolute top-2 right-2">
                <Badge variant="secondary" className="bg-green-500 text-white">
                  <Check className="h-3 w-3 mr-1" />
                  Uploaded
                </Badge>
              </div>
            )}
          </div>
        ) : (
          // Upload Prompt
          <div className="p-8 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              <ImageIcon className="h-6 w-6 text-muted-foreground" />
            </div>
            
            <div className="space-y-2">
              <p className="text-sm font-medium">
                {isDragActive ? 'Drop image here' : 
                 isDragReject ? 'Invalid file type' :
                 'Upload category image'}
              </p>
              <p className="text-xs text-muted-foreground">
                Drag & drop or click to select • JPEG, PNG, WebP • Max 5MB
              </p>
              {categoryName ? (
                <p className="text-xs text-muted-foreground">
                  For: <span className="font-medium">{categoryName}</span>
                </p>
              ) : (
                <p className="text-xs text-yellow-600">
                  Enter a category name first to enable upload
                </p>
              )}
            </div>
          </div>
        )}

      </div>

      {/* Upload Progress */}
      {isUploading && progress && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span>Uploading...</span>
            <span>{Math.round(progressPercentage)}%</span>
          </div>
          <Progress value={progressPercentage} className="h-2" />
        </div>
      )}

      {/* Error Display */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* File Rejection Errors */}
      {fileRejections.length > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {fileRejections[0].errors.map(error => error.message).join(', ')}
          </AlertDescription>
        </Alert>
      )}

      {/* File Info */}
      {selectedFile && !autoUpload && (
        <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary/10 rounded flex items-center justify-center">
              <ImageIcon className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium">{selectedFile.name}</p>
              <p className="text-xs text-muted-foreground">
                {formatFileSize(selectedFile.size)}
              </p>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleManualUpload}
              disabled={isUploading || !categoryName}
            >
              {isUploading ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSelectedFile(null);
                resetUpload();
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Upload Status */}
      {uploadedImage && (
        <div className="flex items-center gap-2 text-sm text-green-600">
          <Check className="h-4 w-4" />
          <span>Image uploaded successfully</span>
          <Badge variant="outline" className="text-xs">
            {formatFileSize(uploadedImage.size)}
          </Badge>
        </div>
      )}
    </div>
  );
};